/**
 * Product Card 3 Manager - Moon Moon Agency
 * Carte produit v3 (snippet mm-product-card-3) : slider des visuels (glisser à la souris + barre de progression), média au survol,
 * vidéos du slider (médias Noël) lues en boucle tant qu'elles sont visibles,
 * et dans un carrousel [data-product-card-3-rail] : à la souris, glisser le texte fait défiler le carrousel
 * et glisser la photo passe au produit suivant une fois la dernière photo atteinte (au doigt, le navigateur fait de même).
 */

/*********************************************************************************************
 * 🃏 PRODUCT CARD 3 MANAGER
 */
if (!window.MMProductCard3Manager) {
	const ProductCard3Manager = {
		config: {
			selectors: {
				media: '[data-product-card-3-media]',
				track: '[data-product-card-3-track]',
				hover: '.mm-product-card-3-hover',
				video: '[data-product-card-3-video]',
				rail: '[data-product-card-3-rail]',
			},
			classes: {
				hovered: 'is-hovered',
				dragging: 'is-dragging',
			},
			progressProperty: '--mm-product-card-3-progress',
		},

		hoverQuery: window.matchMedia('(hover: hover) and (pointer: fine)'),
		reducedMotionQuery: window.matchMedia('(prefers-reduced-motion: reduce)'),
		pendingTracks: new Set(),
		drag: null,
		blockedClickTrack: null,

		/**
		 * Initialize the module
		 */
		init() {
			this._onDragMove = this._moveDrag.bind(this);
			this._onDragEnd = this._endDrag.bind(this);
			this._bindEvents();
			this._observeVideos(document);
			window.log('info', '🃏 [PRODUCT_CARD_3]', 'Initialized');
		},

		_observeVideos(root) {
			const videos = root.querySelectorAll(this.config.selectors.video);
			if (!videos.length || !('IntersectionObserver' in window)) return;
			this.videoObserver ??= new IntersectionObserver((entries) => {
				entries.forEach(({ target, isIntersecting }) => {
					if (isIntersecting) this._playVideo(target);
					else if (!target.paused) target.pause();
				});
			}, { threshold: 0.5 });
			videos.forEach((video) => this.videoObserver.observe(video));
		},

		_playVideo(video) {
			if (this.reducedMotionQuery.matches) return;
			if (!video.getAttribute('src') && video.dataset.src) video.src = video.dataset.src;
			video.muted = true;
			const playPromise = video.play();
			if (playPromise) playPromise.catch(() => {});
		},

		_bindEvents() {
			document.addEventListener('scroll', (e) => {
				const track = e.target;
				if (!(track instanceof Element) || !track.matches(this.config.selectors.track)) return;
				if (this.pendingTracks.has(track)) return;
				this.pendingTracks.add(track);
				requestAnimationFrame(() => {
					this.pendingTracks.delete(track);
					this.updateProgress(track);
					const media = track.closest(this.config.selectors.media);
					if (media && this.hoverQuery.matches && !media.dataset.productCard3HoverDismissed) this.hideHover(media, true);
				});
			}, { capture: true, passive: true });

			document.addEventListener('pointerdown', (e) => this._startDrag(e));

			document.addEventListener('shopify:section:load', (e) => this._observeVideos(e.target));

			document.addEventListener('click', (e) => {
				if (!this.blockedClickTrack) return;
				const blocked = this.blockedClickTrack.contains(e.target);
				this.blockedClickTrack = null;
				if (!blocked) return;
				e.preventDefault();
				e.stopImmediatePropagation();
			}, true);

			document.addEventListener('dragstart', (e) => {
				if (!(e.target instanceof Element)) return;
				if (e.target.closest(this.config.selectors.track) || this._getRail(e.target)) e.preventDefault();
			});

			document.addEventListener('pointerover', (e) => {
				if (e.pointerType !== 'mouse') return;
				const media = e.target.closest(this.config.selectors.media);
				if (!media || media.contains(e.relatedTarget)) return;
				this.showHover(media);
			});

			document.addEventListener('pointerout', (e) => {
				if (e.pointerType !== 'mouse') return;
				const media = e.target.closest(this.config.selectors.media);
				if (!media || media.contains(e.relatedTarget)) return;
				delete media.dataset.productCard3HoverDismissed;
				this.hideHover(media);
			});
		},

		_getRail(element) {
			const rail = element.closest(this.config.selectors.rail);
			return rail && this._isScrollable(rail) ? rail : null;
		},

		_isScrollable(element) {
			return !!element && element.scrollWidth > element.clientWidth + 1;
		},

		_canScrollToward(element, delta) {
			const max = element.scrollWidth - element.clientWidth;
			return delta < 0 ? element.scrollLeft < max - 1 : element.scrollLeft > 1;
		},

		_startDrag(e) {
			this.blockedClickTrack = null;
			if (e.pointerType !== 'mouse' || e.button !== 0 || !(e.target instanceof Element)) return;
			const track = e.target.closest(this.config.selectors.track);
			const rail = this._getRail(e.target);
			if (!track && e.target.closest('button, input, select, textarea, label')) return;
			const scroller = this._isScrollable(track) ? track : rail;
			if (!scroller) return;
			this.drag = {
				scroller,
				rail,
				media: e.target.closest(this.config.selectors.media),
				pointerId: e.pointerId,
				startX: e.clientX,
				lastX: e.clientX,
				startLeft: scroller.scrollLeft,
				startIndex: this._getIndex(scroller),
				moved: false,
			};
			window.addEventListener('pointermove', this._onDragMove);
			window.addEventListener('pointerup', this._onDragEnd);
			window.addEventListener('pointercancel', this._onDragEnd);
		},

		_moveDrag(e) {
			const drag = this.drag;
			if (!drag || e.pointerId !== drag.pointerId) return;
			drag.lastX = e.clientX;
			const delta = e.clientX - drag.startX;
			if (!drag.moved) {
				if (Math.abs(delta) < 5) return;
				if (drag.rail && drag.scroller !== drag.rail && !this._canScrollToward(drag.scroller, delta)) {
					drag.scroller = drag.rail;
					drag.startLeft = drag.rail.scrollLeft;
					drag.startIndex = this._getIndex(drag.rail);
				}
				drag.moved = true;
				window.getSelection()?.removeAllRanges();
				drag.scroller.setPointerCapture(e.pointerId);
				drag.scroller.classList.add(this.config.classes.dragging);
				if (drag.media) this.hideHover(drag.media, true);
			}
			drag.scroller.scrollLeft = drag.startLeft - delta;
		},

		_endDrag(e) {
			const drag = this.drag;
			if (!drag || e.pointerId !== drag.pointerId) return;
			this.drag = null;
			window.removeEventListener('pointermove', this._onDragMove);
			window.removeEventListener('pointerup', this._onDragEnd);
			window.removeEventListener('pointercancel', this._onDragEnd);
			if (!drag.moved) return;

			const { scroller } = drag;
			const delta = drag.lastX - drag.startX;
			let index = this._getIndex(scroller);
			if (index === drag.startIndex && Math.abs(delta) > scroller.clientWidth * 0.15) index += delta < 0 ? 1 : -1;
			index = Math.max(0, Math.min(scroller.children.length - 1, index));

			if (e.type === 'pointerup') this.blockedClickTrack = scroller;
			this._snapTo(scroller, index);
		},

		_snapTo(track, index) {
			const left = this._getOffsets(track)[index] ?? 0;
			const release = () => {
				if (this.drag?.moved && this.drag.scroller === track) return;
				track.classList.remove(this.config.classes.dragging);
			};
			if (this.reducedMotionQuery.matches || Math.abs(track.scrollLeft - left) <= 1) {
				track.scrollLeft = left;
				release();
				return;
			}
			const onScrollEnd = () => {
				if (Math.abs(track.scrollLeft - left) > 1) return;
				track.removeEventListener('scrollend', onScrollEnd);
				release();
			};
			track.addEventListener('scrollend', onScrollEnd);
			setTimeout(() => {
				track.removeEventListener('scrollend', onScrollEnd);
				release();
			}, 1000);
			track.scrollTo({ left, behavior: 'smooth' });
		},

		_getOffsets(scroller) {
			const max = scroller.scrollWidth - scroller.clientWidth;
			const padding = parseFloat(getComputedStyle(scroller).scrollPaddingInlineStart) || 0;
			const origin = scroller.getBoundingClientRect().left - scroller.scrollLeft + padding;
			return [...scroller.children].map((child) => Math.max(0, Math.min(max, Math.round(child.getBoundingClientRect().left - origin))));
		},

		_getIndex(scroller) {
			const offsets = this._getOffsets(scroller);
			let index = 0;
			offsets.forEach((offset, i) => {
				if (Math.abs(offset - scroller.scrollLeft) < Math.abs(offsets[index] - scroller.scrollLeft)) index = i;
			});
			return index;
		},

		updateProgress(track) {
			const media = track.closest(this.config.selectors.media);
			if (!media) return;
			const max = track.scrollWidth - track.clientWidth;
			const progress = max > 0 ? Math.min(1, Math.max(0, track.scrollLeft / max)) : 0;
			media.style.setProperty(this.config.progressProperty, progress.toFixed(3));
		},

		showHover(media) {
			if (!this.hoverQuery.matches || media.dataset.productCard3HoverDismissed) return;
			const hover = media.querySelector(this.config.selectors.hover);
			if (!hover) return;

			if (!(hover instanceof HTMLVideoElement)) {
				media.classList.add(this.config.classes.hovered);
				return;
			}
			if (this.reducedMotionQuery.matches) return;
			if (!hover.dataset.productCard3Bound) {
				hover.dataset.productCard3Bound = 'true';
				hover.addEventListener('playing', () => {
					if (!hover.paused) media.classList.add(this.config.classes.hovered);
				});
			}
			this._playVideo(hover);
		},

		hideHover(media, dismiss = false) {
			if (dismiss) media.dataset.productCard3HoverDismissed = 'true';
			media.classList.remove(this.config.classes.hovered);
			const hover = media.querySelector(this.config.selectors.hover);
			if (hover instanceof HTMLVideoElement && !hover.paused) hover.pause();
		},
	};

	window.MMProductCard3Manager = ProductCard3Manager;

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', () => ProductCard3Manager.init());
	} else {
		ProductCard3Manager.init();
	}
}
